import { GrandstandButton } from '@/components/Buttons'
import { GrandstandInput } from '@/components/Input'
import { VerifyCodeResult } from '@/components/types'
import { MultiStageAuthContext } from '@/pages/_app'
import { SubheadingLabel } from '@/styles/fonts'
import { ApiClient } from '@grandstand-web/bally-web-shared/src/newPackages/ApiClient'
import { AnalyticServiceContext } from '@grandstand-web/bally-web-shared/src/services/analytics/AnalyticService'
import {
  ConfigServiceContext,
  useContextUnconditionally,
} from '@grandstand-web/bally-web-shared/src/services/config/ConfigService'
import React, { useCallback, useContext, useReducer } from 'react'
import styled from 'styled-components'

type VerificationState = 'none' | 'verifying' | 'verified' | 'failed'

type ActivateDeviceProps = {
  onCodeActivated: (result: VerifyCodeResult) => void
  deviceCode?: string
}
const ActivateForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 20px;
`

const ActivateInput = styled(GrandstandInput)`
  width: 100%;
`

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`
const Header = styled.h1`
  margin-top: 15px;
  margin-bottom: 10px;
`
const SubHeader = styled(SubheadingLabel)`
  span {
    text-decoration: var(--underline);
    cursor: pointer;
  }
`
const FormField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  div {
    margin-bottom: 10px;
    width: 100%;
  }
`
const SubmitButton = styled(GrandstandButton)`
  width: 100%;
  margin-bottom: 10px;
`

type State = {
  verificationState: VerificationState
  code: string
  isButtonDisabled: boolean
  isLoading: boolean
}

type Action =
  | { type: 'SET_VERIFICATION_STATE'; payload: VerificationState }
  | { type: 'SET_CODE'; payload: string }
  | { type: 'SET_BUTTON_DISABLED'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_VERIFICATION_STATE':
      return { ...state, verificationState: action.payload }
    case 'SET_CODE':
      return { ...state, code: action.payload.toUpperCase() }
    case 'SET_BUTTON_DISABLED':
      return { ...state, isButtonDisabled: action.payload }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    default:
      return state
  }
}

const ActivateDevice = ({ onCodeActivated, deviceCode }: ActivateDeviceProps) => {
  const [state, dispatch] = useReducer(reducer, {
    verificationState: 'none',
    code: deviceCode ?? '',
    isButtonDisabled: true,
    isLoading: false,
  })
  const { code, isButtonDisabled, isLoading } = state
  const { currentConfig } = useContextUnconditionally(ConfigServiceContext)
  const analyticService = useContext(AnalyticServiceContext)
  const { setPartnerSource } = useContext(MultiStageAuthContext)

  const onChangeCode = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_CODE', payload: e.target.value })
  }, [])

  const handleVerifyCode = useCallback(
    async (code: string) => {
      dispatch({ type: 'SET_VERIFICATION_STATE', payload: 'verifying' })
      try {
        // capture button text for this button is 'Activate'
        analyticService.pinPair({
          step: 2,
          cta: 'account sign in',
        })
        const url = currentConfig.API.services.auth_services.ballys.verify_code
        if (!url || url.length === 0) {
          throw new Error('Invalid config for verifyCode')
        }
        const verifyResponse = await ApiClient.convenientApiFetch({
          url: `${url}?code=${code}`,
          method: 'GET',
        }).then((d) => d.json())
        if (!verifyResponse || (verifyResponse.status && verifyResponse.status !== 200) || code.length === 0) {
          throw new Error('Invalid code')
        }
        if (setPartnerSource && verifyResponse.partner_source) {
          setPartnerSource(verifyResponse.partner_source)
        }
        dispatch({ type: 'SET_VERIFICATION_STATE', payload: 'verified' })
        onCodeActivated(verifyResponse)
      } catch (ex) {
        console.error(ex)
        let error
        if (ex instanceof Error) {
          // Handle specific error types
          error = ex.message
        } else {
          // Handle other types of errors
          error = ex
        }
        analyticService.pinPairError({
          step: 1,
          error_message: error as string,
        })
        dispatch({ type: 'SET_VERIFICATION_STATE', payload: 'failed' })
        alert(`Error verifying code: ${(ex as Error).message}`)
      }
    },
    [analyticService, currentConfig.API.services.auth_services.ballys.verify_code, onCodeActivated, setPartnerSource]
  )

  const onActivateCode = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_LOADING', payload: false })
  }, [])

  return (
    <MainContent>
      <Header>Activate your device</Header>
      <SubHeader fontSize="1">Enter the code displayed on your TV to activate your device.</SubHeader>
      <ActivateForm onSubmit={onActivateCode}>
        <FormField>
          <ActivateInput
            state="default"
            type="code"
            id="code"
            name="code"
            value={code}
            onChange={onChangeCode}
            placeholder="Enter code"
          />
        </FormField>
        <SubmitButton
          buttonStyle="primary"
          buttonSize="sm"
          type="submit"
          disabled={isButtonDisabled}
          loading={isLoading}
          onClick={() => handleVerifyCode(code)}
        >
          Activate
        </SubmitButton>
      </ActivateForm>
    </MainContent>
  )
}
export default ActivateDevice
